/*
 * The circular video row and its full-screen player.
 *
 * Two jobs, deliberately kept apart:
 *
 *   1. The row. Attach and start the looping thumbnails only once the row is
 *      near the viewport, and stop them again when it leaves. The app being
 *      replaced downloads all six loops on every product page whether or not
 *      the shopper ever scrolls to them — about half a megabyte spent before
 *      anyone has looked.
 *
 *   2. The player. One <video> element reused for every clip, so opening the
 *      fourth story does not leave three decoders alive behind it.
 *
 * Nothing here adds to the cart. Each clip's buy row is rendered by Liquid into
 * a <template> and moved into the player as-is, so a clip about another product
 * carries a real <form action="/cart/add"> that assets/hn-cart.js already
 * intercepts, and a clip about THIS product carries a button that hands back to
 * the page's own add to cart — the one that knows which pack the shopper picked.
 * A second cart path here would silently ignore the pack selector.
 */
(function () {
  'use strict';

  var root = document.querySelector('[data-hn-reels]');
  if (!root) return;

  var buttons = Array.prototype.slice.call(root.querySelectorAll('[data-hn-reel]'));
  if (!buttons.length) return;

  var reduceMotion = window.matchMedia
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ------------------------------------------------------------ the row */

  function loopFor(button) { return button.querySelector('.hn-reel__loop'); }

  function startLoop(button) {
    if (reduceMotion) return;
    (function (button) {
      var video = loopFor(button);
      var src = button.getAttribute('data-loop');
      if (!video || !src) return;
      if (!video.getAttribute('src')) {
        video.setAttribute('src', src);
        video.setAttribute('preload', 'auto');
      }
      /*
       * `data-playing` is what fades the video in over the still, and it is set
       * on the `playing` EVENT rather than straight after calling play().
       *
       * Setting it immediately was wrong in the one case that matters: when the
       * browser refuses to autoplay -- battery saver, data saver, an iOS low
       * power mode -- play() rejects, the rejection is swallowed, and the video
       * is faded in anyway, paused on nothing. What the shopper then sees is a
       * circle that never moves, which is exactly the complaint. Waiting for the
       * event means a refusal simply leaves the still showing, which is a
       * perfectly good picture, and the circle animates the moment it can.
       */
      if (!video.hasAttribute('data-bound')) {
        video.setAttribute('data-bound', '');
        video.addEventListener('playing', function () { video.setAttribute('data-playing', ''); });
        video.addEventListener('pause', function () { video.removeAttribute('data-playing'); });
        /*
         * Some browsers will not honour a play() issued before the first frame
         * is decodable, and answer with a rejection rather than a queue. Asking
         * again once there is something to show turns that into a delay instead
         * of a dead circle.
         */
        video.addEventListener('canplay', function () {
          if (video.paused) video.play().catch(function () {});
        });
      }
      var started = video.play();
      if (started && started.catch) started.catch(function () {});
    })(button);
  }

  function stopLoop(button) {
    var video = loopFor(button);
    if (!video) return;
    video.pause();
    video.removeAttribute('data-playing');
  }

  function stopLoops() { buttons.forEach(stopLoop); }

  /*
   * Two observers, because there are two different questions.
   *
   *   1. Is the row anywhere near the viewport? If not, nothing at all is
   *      fetched -- the rootMargin pulls the boundary down the page so the
   *      loops are already moving by the time the row is looked at.
   *
   *   2. Which circles are actually visible INSIDE the row? The row scrolls
   *      sideways and typically shows three or four of them, but the first
   *      version loaded every clip in it the moment the row appeared. On this
   *      store's busiest product that is 790 KB of video -- about a second and a
   *      half of a 4G link -- spent on circles the shopper may never scroll to,
   *      and spent at the exact moment they are reaching for Add to cart, which
   *      is the tap they then find slow.
   *
   * The second observer is rooted on the row itself, so "visible" means visible
   * in the strip rather than in the window.
   */
  var perCircle = null;
  if ('IntersectionObserver' in window) {
    var strip = root.querySelector('.hn-reels__row') || root;
    perCircle = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) startLoop(entry.target);
        else stopLoop(entry.target);
      });
    }, { root: strip, rootMargin: '50% 0px' });

    new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) buttons.forEach(function (b) { perCircle.observe(b); });
        else { buttons.forEach(function (b) { perCircle.unobserve(b); }); stopLoops(); }
      });
    }, { rootMargin: '200px 0px' }).observe(root);
  } else {
    buttons.forEach(startLoop);
  }

  /*
   * A backgrounded tab keeps six decoders alive for no one. This also covers
   * the phone being locked, which is the same thing from the page's side.
   */
  document.addEventListener('visibilitychange', function () {
    if (document.hidden) stopLoops();
  });

  /* --------------------------------------------------------- the player */

  var clips = buttons.map(function (button, index) {
    var template = root.querySelector('[data-hn-reel-card="' + index + '"]');
    return {
      video: button.getAttribute('data-video'),
      poster: button.getAttribute('data-poster'),
      caption: button.getAttribute('data-caption') || '',
      card: template ? template.content : null
    };
  }).filter(function (clip) { return clip.video; });

  if (!clips.length) return;

  /*
   * Inline SVG rather than the speaker emoji. An emoji is rendered by whatever
   * font the device happens to have, and on this store's own screenshot the
   * muted speaker came out as a red mark that reads as an error rather than as a
   * control. A path draws the same on every device and inherits currentColor.
   */
  var ICON_MUTED =
    '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" ' +
    'stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
    '<path d="M11 5 6 9H3v6h3l5 4z"/><path d="m16 9 5 6"/><path d="m21 9-5 6"/></svg>';
  var ICON_LOUD =
    '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" ' +
    'stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
    '<path d="M11 5 6 9H3v6h3l5 4z"/><path d="M16 8.5a4 4 0 0 1 0 7"/>' +
    '<path d="M19 6a8 8 0 0 1 0 12"/></svg>';
  var ICON_CLOSE =
    '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" ' +
    'stroke-width="2" stroke-linecap="round" aria-hidden="true">' +
    '<path d="m6 6 12 12M18 6 6 18"/></svg>';
  var ICON_PREV =
    '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" ' +
    'stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
    '<path d="m15 5-7 7 7 7"/></svg>';
  var ICON_NEXT =
    '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" ' +
    'stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
    '<path d="m9 5 7 7-7 7"/></svg>';

  var player = null;
  var media = null;
  var ticks = [];
  var current = -1;
  var lastFocus = null;

  /*
   * Stories open WITH sound. These are people talking -- a silent one is a
   * different thing entirely.
   *
   * Autoplay with sound is allowed only off a user gesture, and opening a story
   * is one: the shopper tapped the circle. Where a browser refuses anyway, the
   * refusal is caught and the clip is restarted muted rather than left frozen,
   * and the button is put back to "unmute" so the choice is one tap away. That
   * fallback is the whole reason `muted` is a variable and not a constant.
   */
  var muted = false;

  function build() {
    player = document.createElement('div');
    player.className = 'hn-player';
    player.setAttribute('role', 'dialog');
    player.setAttribute('aria-modal', 'true');
    player.setAttribute('aria-label', 'Product videos');
    player.innerHTML =
      '<div class="hn-player__progress" aria-hidden="true"></div>' +
      '<button class="hn-player__mute" type="button" aria-label="Unmute">' + ICON_MUTED + '</button>' +
      '<button class="hn-player__close" type="button" aria-label="Close">' + ICON_CLOSE + '</button>' +
      '<button class="hn-player__nav hn-player__nav--prev" type="button" aria-label="Previous video">' + ICON_PREV + '</button>' +
      '<button class="hn-player__nav hn-player__nav--next" type="button" aria-label="Next video">' + ICON_NEXT + '</button>' +
      '<div class="hn-player__stage">' +
        '<video class="hn-player__video" playsinline preload="none"></video>' +
      '</div>' +
      '<p class="hn-player__caption"></p>' +
      '<div class="hn-player__card"></div>';
    document.body.appendChild(player);

    media = player.querySelector('.hn-player__video');
    media.muted = muted;

    var bar = player.querySelector('.hn-player__progress');
    ticks = clips.map(function () {
      var tick = document.createElement('span');
      tick.className = 'hn-player__tick';
      tick.appendChild(document.createElement('i'));
      bar.appendChild(tick);
      return tick.firstChild;
    });

    player.querySelector('.hn-player__close').addEventListener('click', close);
    player.querySelector('.hn-player__nav--prev').addEventListener('click', function () { go(current - 1); });
    player.querySelector('.hn-player__nav--next').addEventListener('click', function () { go(current + 1); });
    player.querySelector('.hn-player__mute').addEventListener('click', toggleMute);

    paintMuteButton();

    media.addEventListener('timeupdate', paint);
    media.addEventListener('ended', function () { go(current + 1); });
    /*
     * Tap the left third to go back, the right third to go on, the middle to
     * pause -- the way every stories player works, and what the shopper will
     * try first. Bound to the stage rather than to the <video>, because the
     * video is letterboxed inside it and a tap on the black margin either side
     * is still a tap on that side of the screen.
     *
     * `closest` guards the controls: the close, mute and buy row all sit above
     * the stage, and a tap on one of them must not also count as navigation.
     */
    var stage = player.querySelector('.hn-player__stage');
    stage.addEventListener('click', function (event) {
      if (event.target.closest('button, a, .hn-player__card')) return;
      var box = stage.getBoundingClientRect();
      var across = (event.clientX - box.left) / box.width;
      if (across < 0.3) { go(current - 1); return; }
      if (across > 0.7) { go(current + 1); return; }
      if (media.paused) media.play().catch(function () {}); else media.pause();
    });

    bindSwipe();
  }

  function paint() {
    if (current < 0 || !ticks[current]) return;
    var ratio = media.duration ? (media.currentTime / media.duration) : 0;
    ticks[current].style.width = Math.min(100, ratio * 100) + '%';
  }

  function paintMuteButton() {
    var button = player.querySelector('.hn-player__mute');
    if (!button) return;
    button.innerHTML = muted ? ICON_MUTED : ICON_LOUD;
    button.setAttribute('aria-label', muted ? 'Unmute' : 'Mute');
  }

  function toggleMute() {
    muted = !muted;
    media.muted = muted;
    paintMuteButton();
    if (!muted) media.play().catch(function () {});
  }

  function go(index) {
    if (index < 0) index = 0;
    if (index >= clips.length) { close(); return; }

    var clip = clips[index];
    current = index;

    ticks.forEach(function (tick, i) { tick.style.width = i < index ? '100%' : '0'; });

    media.pause();
    if (clip.poster) media.setAttribute('poster', clip.poster);
    media.setAttribute('src', clip.video);
    media.muted = muted;
    media.currentTime = 0;
    media.play().catch(function () {
      /*
       * Refused with sound. Fall back rather than leave a still frame: a muted
       * clip that plays is worth more than a silent one that does not.
       */
      if (muted) return;
      muted = true;
      media.muted = true;
      paintMuteButton();
      media.play().catch(function () {});
    });

    player.querySelector('.hn-player__caption').textContent = clip.caption.trim();

    var card = player.querySelector('.hn-player__card');
    card.textContent = '';
    if (clip.card) card.appendChild(clip.card.cloneNode(true));

    /*
     * The clip about the product whose page this is hands back to the page's own
     * add to cart. Closing first matters: the theme opens its side cart on a
     * successful add, and a drawer underneath a full-screen player is a drawer
     * nobody can see.
     */
    var handoff = card.querySelector('[data-hn-reel-use-page-atc]');
    if (handoff) {
      handoff.addEventListener('click', function () {
        close();
        var pageAtc = document.querySelector(
          'form[action*="/cart/add"] [type="submit"], form[action*="/cart/add"] button');
        if (pageAtc) pageAtc.click();
      });
    }

    player.querySelector('.hn-player__nav--prev').hidden = index === 0;
    player.querySelector('.hn-player__nav--next').hidden = index === clips.length - 1;
  }

  function open(index) {
    if (!player) build();
    lastFocus = document.activeElement;
    player.setAttribute('data-open', '');
    document.body.classList.add('hn-player-open');
    go(index);
    player.querySelector('.hn-player__close').focus();
    document.addEventListener('keydown', onKey);
  }

  function close() {
    if (!player) return;
    media.pause();
    media.removeAttribute('src');
    media.load();
    player.removeAttribute('data-open');
    /*
     * Removing the class is what gives the page its scrolling back. Leaving it
     * behind is the classic modal bug: the overlay is gone and the page is dead.
     */
    document.body.classList.remove('hn-player-open');
    document.removeEventListener('keydown', onKey);
    current = -1;
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  function onKey(event) {
    if (event.key === 'Escape') { close(); return; }
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') { go(current + 1); return; }
    if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') { go(current - 1); return; }
    if (event.key !== 'Tab') return;

    var focusable = player.querySelectorAll(
      'button:not([hidden]), a[href], input, [tabindex]:not([tabindex="-1"])');
    if (!focusable.length) return;
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      last.focus();
      event.preventDefault();
    } else if (!event.shiftKey && document.activeElement === last) {
      first.focus();
      event.preventDefault();
    }
  }

  function bindSwipe() {
    var startY = 0;
    var startX = 0;
    var tracking = false;

    player.addEventListener('touchstart', function (event) {
      if (event.touches.length !== 1) return;
      tracking = true;
      startY = event.touches[0].clientY;
      startX = event.touches[0].clientX;
    }, { passive: true });

    player.addEventListener('touchend', function (event) {
      if (!tracking) return;
      tracking = false;
      var touch = event.changedTouches[0];
      var dy = touch.clientY - startY;
      var dx = touch.clientX - startX;
      /*
       * Vertical, to match the app being replaced (`swipe_direction: vertical`).
       * The horizontal comparison stops a diagonal drag across the buy row from
       * counting as a swipe.
       */
      if (Math.abs(dy) < 60 || Math.abs(dy) < Math.abs(dx)) return;
      go(dy < 0 ? current + 1 : current - 1);
    }, { passive: true });
  }

  buttons.forEach(function (button, index) {
    button.addEventListener('click', function () { open(index); });
  });
})();
